import { FactoryProvider, inject } from '@angular/core';
import { EG_ASSISTANT_SUGGESTIONS, EgAssistantSuggestionsResolver } from '@expertgroup/assistant';
import { CopyService } from '@/app/core/copy/copy.service';

/**
 * Ponuđena pitanja po ekranu (AB#7598) — šta Ljiljana nudi u praznom razgovoru, po id-ju ekrana iz registra.
 *
 * Biblioteka prikazuje najviše tri, samo dok nit nema nijedan potez, i šalje tekst DOSLOVNO tek na klik. Zato
 * je ovde spisak KLJUČEVA kopije, a ne tekst: pitanje se prevodi kao i svaki drugi tekst, a ključ stoji kao
 * doslovan string da bi ga `check:copy` našao (sastavljen ključ bi prijavio kao neupotrebljen).
 *
 * Dijalog za uvoz prijavljuje svoj ekran preko egAssistantDialogScreen, pa dobija svoja pitanja.
 * Ostali dijalozi koriste kontekst roditeljskog ekrana dok ne prijave svoj kontekst.
 */
export const SUGGESTION_KEYS: Readonly<Record<string, readonly string[]>> = {
    'protax.dashboard': ['assistant.suggestions.protax.dashboard.1', 'assistant.suggestions.protax.dashboard.2', 'assistant.suggestions.protax.dashboard.3'],
    'protax.properties.list': ['assistant.suggestions.protax.properties.list.1', 'assistant.suggestions.protax.properties.form.1', 'assistant.suggestions.protax.properties.import.1'],
    'protax.tax-returns.list': ['assistant.suggestions.protax.tax-returns.create.1', 'assistant.suggestions.protax.tax-returns.amend-base.1', 'assistant.suggestions.protax.tax-returns.submit.1'],
    'protax.zones': ['assistant.suggestions.protax.zones.1', 'assistant.suggestions.protax.zones.2', 'assistant.suggestions.protax.zones.3'],
    'protax.admin.entities': ['assistant.suggestions.protax.admin.entities.1', 'assistant.suggestions.protax.admin.entities.2', 'assistant.suggestions.protax.admin.entities.plan.1'],

    // ── Dijalozi — danas nedostižni (vidi gore), ali potpuni ─────────────────────────────────────────────
    'protax.properties.form': ['assistant.suggestions.protax.properties.form.1', 'assistant.suggestions.protax.properties.form.2', 'assistant.suggestions.protax.properties.form.3'],
    'protax.properties.import': ['assistant.suggestions.protax.properties.import.1', 'assistant.suggestions.protax.properties.import.2'],
    'protax.tax-returns.create': ['assistant.suggestions.protax.tax-returns.create.1', 'assistant.suggestions.protax.tax-returns.create.2'],
    'protax.tax-returns.submit': ['assistant.suggestions.protax.tax-returns.submit.1', 'assistant.suggestions.protax.tax-returns.submit.2'],
    'protax.tax-returns.amend-base': ['assistant.suggestions.protax.tax-returns.amend-base.1', 'assistant.suggestions.protax.tax-returns.amend-base.2', 'assistant.suggestions.protax.tax-returns.amend-base.3'],
    // Promena plana podrške nije tema propisa; pitanja su o poreskoj obavezi pravnog lica čiji se plan menja.
    'protax.admin.entities.plan': ['assistant.suggestions.protax.admin.entities.plan.1', 'assistant.suggestions.protax.admin.entities.plan.2']
};

/** Resolver nad kopijom — čista funkcija, da je spec vozi i bez DI-ja. */
export function protaxSuggestions(t: (key: string) => string): EgAssistantSuggestionsResolver {
    return (screen) => {
        const id = screen?.screenId;
        // ⚠️ `Object.hasOwn`, ne `SUGGESTION_KEYS[id]`: id `constructor` bi inače vratio funkciju iz prototipa.
        if (!id || !Object.hasOwn(SUGGESTION_KEYS, id)) return [];
        return SUGGESTION_KEYS[id].map((key) => t(key));
    };
}

/**
 * Registracija za `app.config.ts`.
 *
 * ⚠️ Zapis provajdera, ne `provideEgAssistantSuggestions(...)`: ta funkcija prima GOTOV resolver (`useValue`),
 * a ovom treba `CopyService` iz DI-ja. Fabrika se izvršava lenjo, kad traka prvi put zatraži token, pa
 * bezuslovna registracija ne košta ništa kad je asistent isključen — isto kao `provideEgAssistantScreen`.
 * Jezik se bira jednom, pri pokretanju (`language.ts`), pa je rečnik koji fabrika uhvati uvek tekući.
 */
export const protaxSuggestionsProvider: FactoryProvider = {
    provide: EG_ASSISTANT_SUGGESTIONS,
    useFactory: () => protaxSuggestions(inject(CopyService).t)
};
